import { FormLocalizationProvider } from '@faceup/form'
import { defineMessages, useIntl } from '@faceup/localization'
import {
  EnvironmentMessage,
  UiProvider as FUUiProvider,
  type PagesTranslationItems,
  type PagesTranslationVariants,
  type UiProviderProps,
} from '@faceup/ui'
import { UiBaseProvider } from '@faceup/ui-base'
import type { Institution, Plan } from '@faceup/utils'
import { type ReactNode, useContext, useState } from 'react'
import { sharedMessages } from '../Shared/translations'
import { LanguageContext } from './LanguageContext'

const messages = defineMessages('followUp.appProviders', [
  { id: 'confirmRedirectMessageTitle', defaultMessage: '' },
  { id: 'confirmRedirectMessageContent', defaultMessage: '' },
  { id: 'redirectOkButton', defaultMessage: '' },
  { id: 'redirectCancelButton', defaultMessage: '' },
  { id: 'ok', defaultMessage: '' },
  { id: 'cancel', defaultMessage: '' },
  {
    id: 'unsavedChangesCount',
    defaultMessage: '',
    values: {
      count: true,
    },
  },
  { id: 'leaveQuestion', defaultMessage: '' },
  { id: 'leaveTitle', defaultMessage: '' },
  { id: 'uploadInputText', defaultMessage: '' },
] as const)

const messagesPages = {
  freeSchool: defineMessages('followUp.appProviders.pages.freeSchool', [
    { id: 'title', defaultMessage: '' },
    { id: 'content', defaultMessage: '' },
  ] as const),
  unregisteredSchool: defineMessages('followUp.appProviders.pages.unregisteredSchool', [
    { id: 'title', defaultMessage: '' },
    { id: 'content', defaultMessage: '' },
  ] as const),
  testingSchool: defineMessages('followUp.appProviders.pages.testingSchool', [
    { id: 'title', defaultMessage: '' },
    { id: 'content', defaultMessage: '' },
  ] as const),
  school: defineMessages('followUp.appProviders.pages.school', [
    { id: 'title', defaultMessage: '' },
    { id: 'content', defaultMessage: '' },
  ] as const),
  company: defineMessages('followUp.appProviders.pages.company', [
    { id: 'title', defaultMessage: '' },
    { id: 'content', defaultMessage: '' },
  ] as const),
  surveyBeforeSend: defineMessages('followUp.appProviders.pages.surveyBeforeSend', [
    { id: 'title', defaultMessage: '' },
    { id: 'content', defaultMessage: '' },
  ] as const),
  surveyAfterSend: defineMessages('followUp.appProviders.pages.surveyAfterSend', [
    { id: 'title', defaultMessage: '' },
    { id: 'content', defaultMessage: '' },
  ] as const),
} satisfies Record<
  Exclude<PagesTranslationVariants, 'formItems'>,
  Record<PagesTranslationItems, unknown>
>

type UiProviderProviderProps = {
  children: ReactNode
  /** resolved primary color (reportFormColor -> institution -> partner) */
  primaryColor?: string
  institutionName: string
  mother: {
    type: Institution | null
    isTesting: boolean | null
    plan: Plan | null
  }
}

/**
 * Top-down theming and UI localization, reused by the report and survey branches.
 * Colors and branding come straight from the init query (resolved by the branch
 * wrapper).
 */
export const UiProvider = ({
  children,
  primaryColor,
  institutionName: name,
  mother,
}: UiProviderProviderProps) => {
  const { formatMessage } = useIntl()
  const { antLocale, direction } = useContext(LanguageContext)
  const [isDefaultForm, setIsDefaultForm] = useState(false)

  const translations: NonNullable<UiProviderProps['pages']>['translations'] = {
    company: {
      title: formatMessage(messagesPages.company.title),
      content: formatMessage(messagesPages.company.content).split('{name}').join(name),
    },
    school: {
      title: formatMessage(messagesPages.school.title),
      content: formatMessage(messagesPages.school.content).split('{name}').join(name),
    },
    testingSchool: {
      title: formatMessage(messagesPages.testingSchool.title),
      content: formatMessage(messagesPages.testingSchool.content).split('{name}').join(name),
    },
    freeSchool: {
      title: formatMessage(messagesPages.freeSchool.title),
      content: formatMessage(messagesPages.freeSchool.content).split('{name}').join(name),
    },
    unregisteredSchool: {
      title: formatMessage(messagesPages.unregisteredSchool.title),
      content: formatMessage(messagesPages.unregisteredSchool.content).split('{name}').join(name),
    },
    surveyBeforeSend: {
      title: formatMessage(messagesPages.surveyBeforeSend.title),
      content: formatMessage(messagesPages.surveyBeforeSend.content),
    },
    surveyAfterSend: {
      title: formatMessage(messagesPages.surveyAfterSend.title),
      content: formatMessage(messagesPages.surveyAfterSend.content),
    },
    formItems: {
      title: '',
      content: '',
    },
  }

  return (
    <UiBaseProvider colorPrimary={primaryColor} locale={antLocale} direction={direction}>
      <FUUiProvider
        linkExternalConfirm={{
          confirmRedirectMessageTitle: formatMessage(messages.confirmRedirectMessageTitle),
          confirmRedirectMessageContent: formatMessage(messages.confirmRedirectMessageContent),
          redirectOkButton: formatMessage(messages.redirectOkButton),
          redirectCancelButton: formatMessage(messages.redirectCancelButton),
        }}
        pages={{
          mother,
          isDefaultForm,
          setIsDefaultForm,
          translations,
        }}
        buttonConfirm={{
          confirm: formatMessage(messages.ok),
          cancel: formatMessage(messages.cancel),
        }}
        attachmentList={{
          download: formatMessage(sharedMessages.download),
        }}
      >
        <FormLocalizationProvider
          submitButton={{
            send: formatMessage(sharedMessages.send),
            add: formatMessage(sharedMessages.add),
            save: formatMessage(sharedMessages.save),
          }}
          successMessage={{
            send: formatMessage(sharedMessages.savedMessage),
            add: formatMessage(sharedMessages.savedMessage),
            save: formatMessage(sharedMessages.savedMessage),
          }}
          modalForm={{
            cancelButton: formatMessage(sharedMessages.cancel),
          }}
          uploadInput={{
            text: formatMessage(messages.uploadInputText),
          }}
          unsavedChanges={count =>
            formatMessage(messages.unsavedChangesCount, { values: { count } })
          }
          leaveUnsavedChanges={formatMessage(messages.leaveQuestion)}
          leaveUnsavedChangesTitle={formatMessage(messages.leaveTitle)}
        >
          {children}
          {['stage', 'preview'].includes(import.meta.env.VITE_ENVIRONMENT ?? '') && (
            <EnvironmentMessage environment={import.meta.env.VITE_ENVIRONMENT ?? ''} />
          )}
        </FormLocalizationProvider>
      </FUUiProvider>
    </UiBaseProvider>
  )
}
